<template>
    <v-container fluid>
        <v-card class="ma-1 mt-0">
            <v-card-text class="pa-0">
                <v-overlay absolute v-if="loading">
                    <v-progress-circular
                        indeterminate
                        :size="50"
                        :width="5"
                        color="primary"
                    >
                    </v-progress-circular>
                </v-overlay>
                <v-row class="ma-0">
                    <v-col cols="4" class="py-0 my-0 d-flex align-center">
                        <v-card-title>{{ title }}</v-card-title>
                    </v-col>
                    <v-col cols="3" class="py-0 my-0 mt-2">
                        <v-text-field
                            v-model="filter.search"
                            :label="$t('Search') + '...'"
                            prepend-icon="mdi-magnify"
                            class="ml-2"
                            v-debounce:300ms="changeFilter"
                        ></v-text-field>
                    </v-col>
                    <v-col class="py-0 my-0 mt-2">
                        <DatePickerISOCompliant
                            :label="$t('From')"
                            clearable
                            class="ml-2"
                            v-model="filter.from"
                            @change="changeFilter"
                        />
                    </v-col>
                    <v-col class="py-0 my-0 mt-2">
                        <DatePickerISOCompliant
                            :label="$t('To')"
                            clearable
                            class="ml-2"
                            v-model="filter.to"
                            @change="changeFilter"
                        />
                    </v-col>
                    <v-col class="py-0 my-0 mt-2">
                        <v-btn
                            small
                            color="success"
                            @click.prevent="indexSelectedDocuments"
                            :loading="indexationLoading"
                            class="mt-4"
                            width="120"
                        >
                            <v-icon left>mdi-history</v-icon>
                            {{ $t('Retry') }}
                        </v-btn>
                    </v-col>
                </v-row>
                <simple-table
                    :ref="nonIndexedTableKey"
                    :tableConfig="nonIndexedTableConfig"
                    :fetchData="getData"
                    :tableData="tableData"
                ></simple-table>
            </v-card-text>
        </v-card>
    </v-container>
</template>

  <script>
import DocumentIndexationService from '@/services/DocumentIndexationService';
import SearchType from '@/ecm/variables/SearchType';
import Utils from '@/ecm/common/Utils';
import SimpleTable from '@/components/base/SimpleTable.vue';
import DatePickerISOCompliant from '@/components/base/DatePickerISOCompliant.vue';

export default {
    components: {
        SimpleTable,
        DatePickerISOCompliant
    },
    data() {
        return {
            indexationLoading: false,
            nonIndexedTableKey: 'nonIndexedDocumentList',
            allIndexationLoading: false,
            title: this.$t('NonIndexedDocument'),
            dataSource: [],
            loading: false,
            filter: {
                from: null,
                to: null,
                search: null,
            },
            pagination: {
                page: 1,
                pageSize: 50,
            },
            tableData: []
        };
    },
    computed: {
        nonIndexedTableInstance() {
            return this.$refs[this.nonIndexedTableKey];
        },
        nonIndexedTableConfig() {
            const tableHeaders = [];
            tableHeaders.push({ title: 'FileName', titleTranslation: 'Name', allowSorting: true });
            tableHeaders.push({ title: 'ContentType', titleTranslation: 'ContentType', allowSorting: true });
            tableHeaders.push({ title: 'UserName', titleTranslation: 'User', allowSorting: true });
            tableHeaders.push({ title: 'IndexationFailReason', titleTranslation: 'IndexationFailReason', allowSorting: true });
            tableHeaders.push({ title: 'CreationDate', titleTranslation: 'Date', allowSorting: true });
            tableHeaders.push({ title: 'VersionNumber', titleTranslation: 'Version', allowSorting: true });

            return {
                dense: false,
                allowSelection: true,
                selectAllTitleTranslation: 'Documents',
                showActions: true,
                showPagination: true,
                allowSorting: true,
                headers: tableHeaders,
            };
        }
    },
    methods: {
        getRoute(id) {
            const pattern = {
                searchPattern: id,
                searchAllVersions: true,
                allowDeleted: true,
            };
            let route = '/search/';
            route += `${SearchType.DocumentIDs}/${encodeURIComponent(JSON.stringify(pattern))}`;
            return route;
        },
        async getData(pagination) {
            this.loading = true;
            pagination.filter = this.filter.search;
            pagination.dateFrom = this.filter.from;
            pagination.dateTo = this.filter.to;

            const data = await DocumentIndexationService.getNonIndexedGrid(pagination);
            this.loading = false;
            if (!data) {
                return;
            }
            this.dataSource = data.Result;
            this.tableData = this.transformTableData(data.Result);
            return {
                totalItems: data.Count,
                totalPages: Math.ceil(data.Count / pagination.pageSize),
                result: data.Result
            };
        },
        transformTableData(rows) {
            return rows.map(row => {
                const tableFields = [
                    { title: row.FileName, },
                    { title: row.ContentType, },
                    { title: row.UserName, },
                    { title: row.IndexationFailReason, },
                    { title: row.CreationDate, function: () => this.calculateCellValue(row.CreationDate), },
                    { title: row.VersionNumber, },
                ];

                const tableFieldActions = [
                    { icon: 'mdi-eye', color: 'blue', function: this.getRoute(row.Id), translation: 'View', redirect: true },
                    { icon: 'mdi-history', color: 'success', function: () => this.indexationItem(row.Id), translation: 'Indexation', redirect: false },
                ];

                return {
                    id: row.Id,
                    fields: tableFields,
                    actions: tableFieldActions,
                    isSelected: false
                };
            });
        },
        indexationItem(id) {
            this.$confirm(this.$t('DocumentsIndexationConfirmation'), {
                title: this.$t('Indexation'),
                icon: 'mdi-alert',
                color: 'warning',
                buttonTrueText: this.$t('Indexation'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    DocumentIndexationService.indexation([id]).then(
                        (result) => {
                            this.nonIndexedTableInstance.refreshPage();
                        }
                    );
                }
            });
        },
        indexSelectedDocuments() {
            const selectedItems = this.tableData
                .filter((x) => x.isSelected)
                .map((t) => t.id);
            if (selectedItems.length === 0) {
                Utils.toast.warning(this.$t('ItemsSelectionError'));
                return;
            }

            this.$confirm(this.$t('DocumentsIndexationConfirmation'), {
                title: this.$t('Indexation'),
                icon: 'mdi-alert',
                color: 'warning',
                buttonTrueText: this.$t('Indexation'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    this.indexationLoading = true;
                    DocumentIndexationService.indexation(selectedItems)
                        .then((result) => {
                            this.nonIndexedTableInstance.refreshPage();
                        })
                        .catch(() => {
                            // error
                        })
                        .then(() => {
                            //always
                            this.indexationLoading = false;
                        });
                }
            });
        },
        calculateCellValue(delDate) {
            return Utils.date.toUserFormatFromIso(delDate);
        },
        changeFilter() {
            this.nonIndexedTableInstance.resetPagination();
        },
    },
};
</script>
